import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Moveable from "react-moveable";
import { api, apiError, imgUrl, fileToB64 } from "../lib/api";
import { StyleSelect } from "../components/StyleSelect";
import { itemTransform, BoardItemContent } from "../components/moodboard/boardItem";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Sparkles, Image as ImageIcon, Search, Shirt, SwatchBook, Type, Palette,
  Share2, Download, Trash2, ChevronUp, ChevronDown, Copy, Wand2, Loader2, FileDown, X,
} from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);
const TABS = [
  { key: "upload", label: "Upload", icon: Upload },
  { key: "ai", label: "AI image", icon: Sparkles },
  { key: "stock", label: "Stock", icon: Search },
  { key: "designs", label: "Designs", icon: Shirt },
  { key: "materials", label: "Swatches", icon: SwatchBook },
  { key: "text", label: "Text", icon: Type },
  { key: "color", label: "Color", icon: Palette },
];

export default function MoodboardEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [targetEl, setTargetEl] = useState(null);
  const [tab, setTab] = useState("upload");
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(0.6);

  const nodes = useRef({});
  const frame = useRef({ translate: [0, 0], rotate: 0, width: 0, height: 0 });
  const saveTimer = useRef(null);
  const boardRef = useRef(null);

  useEffect(() => {
    api.get(`/moodboards/${id}`).then((r) => { setBoard(r.data); setItems(r.data.items || []); })
      .catch((e) => { toast.error(apiError(e)); navigate("/moodboards"); });
  }, [id, navigate]);

  useEffect(() => { setTargetEl(selectedId ? nodes.current[selectedId] : null); }, [selectedId, items]);

  const persist = useCallback((nextItems, extra = {}) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaving(true);
    saveTimer.current = setTimeout(async () => {
      try { await api.patch(`/moodboards/${id}`, { items: nextItems, ...extra }); }
      catch (e) { toast.error(apiError(e)); }
      finally { setSaving(false); }
    }, 500);
  }, [id]);

  const commitItems = useCallback((next, extra) => { setItems(next); persist(next, extra); }, [persist]);
  const editItem = (itemId, patch) => commitItems(items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)));

  const addItem = (partial) => {
    const base = { id: uid(), x: 120 + items.length * 18, y: 120 + items.length * 14, width: 300, height: 300, rotation: 0, z: (items.length ? Math.max(...items.map((i) => i.z || 1)) : 0) + 1 };
    const next = [...items, { ...base, ...partial }];
    commitItems(next);
    setSelectedId(next[next.length - 1].id);
  };

  const deleteSelected = () => { if (!selectedId) return; commitItems(items.filter((i) => i.id !== selectedId)); setSelectedId(null); };
  const duplicateSelected = () => {
    const it = items.find((i) => i.id === selectedId); if (!it) return;
    const copy = { ...it, id: uid(), x: it.x + 24, y: it.y + 24, z: (Math.max(...items.map((i) => i.z || 1))) + 1 };
    commitItems([...items, copy]); setSelectedId(copy.id);
  };
  const bring = (dir) => { const it = items.find((i) => i.id === selectedId); if (!it) return; editItem(selectedId, { z: Math.max(1, (it.z || 1) + dir) }); };

  const select = (itemId) => {
    setSelectedId(itemId);
    const it = items.find((i) => i.id === itemId);
    if (it) frame.current = { translate: [it.x, it.y], rotate: it.rotation || 0, width: it.width, height: it.height };
  };
  const commitGeom = () => {
    const f = frame.current;
    editItem(selectedId, { x: Math.round(f.translate[0]), y: Math.round(f.translate[1]), width: Math.round(f.width), height: Math.round(f.height), rotation: Math.round(f.rotate * 10) / 10 });
  };

  const selected = items.find((i) => i.id === selectedId);

  // ---- export ----
  const [exporting, setExporting] = useState(false);
  const doExport = async (type) => {
    setSelectedId(null); setExporting(true);
    await new Promise((r) => setTimeout(r, 150));
    try {
      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(boardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: board.background });
      if (type === "png") {
        const a = document.createElement("a"); a.href = dataUrl; a.download = `${board.name}.png`; a.click();
      } else {
        const { jsPDF } = await import("jspdf");
        const pdf = new jsPDF({ orientation: board.canvas_w >= board.canvas_h ? "landscape" : "portrait", unit: "px", format: [board.canvas_w, board.canvas_h] });
        pdf.addImage(dataUrl, "PNG", 0, 0, board.canvas_w, board.canvas_h);
        pdf.save(`${board.name}.pdf`);
      }
      toast.success(`Exported ${type.toUpperCase()}`);
    } catch (e) { toast.error("Export failed — try removing cross-origin images"); }
    finally { setExporting(false); }
  };

  const share = async () => {
    try {
      const { data } = await api.post(`/moodboards/${id}/share`, { enable: true });
      const url = `${window.location.origin}/board/${data.share_id}`;
      try { await navigator.clipboard.writeText(url); toast.success("Public link copied to clipboard"); }
      catch { toast.success("Public link ready"); window.prompt("Share link:", url); }
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!board) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-zinc-100" data-testid="moodboard-editor">
      {/* top bar */}
      <div className="h-14 shrink-0 bg-white border-b border-zinc-200 flex items-center justify-between px-4 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="moodboard-back" onClick={() => navigate("/moodboards")} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"><ArrowLeft size={18} /></button>
          <input data-testid="moodboard-name" value={board.name}
            onChange={(e) => setBoard({ ...board, name: e.target.value })}
            onBlur={(e) => api.patch(`/moodboards/${id}`, { name: e.target.value }).catch(() => {})}
            className="font-heading font-semibold text-lg bg-transparent focus:outline-none focus:bg-zinc-50 rounded px-1 min-w-0 truncate" />
          <span className="text-xs text-zinc-400 shrink-0">{saving ? "Saving…" : "Saved"}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-1 mr-1">
            <button onClick={() => setZoom((z) => Math.max(0.25, z - 0.1))} className="w-7 h-7 rounded border border-zinc-200 text-zinc-600 hover:bg-zinc-100 text-sm">−</button>
            <span className="text-xs text-zinc-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(1, z + 0.1))} className="w-7 h-7 rounded border border-zinc-200 text-zinc-600 hover:bg-zinc-100 text-sm">+</button>
          </div>
          <button data-testid="moodboard-to-collection" onClick={() => navigate(`/studio/collection?board=${id}`)}
            className="inline-flex items-center gap-1.5 text-sm border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100" title="Use this board to seed an AI collection">
            <Shirt size={14} /> Design Collection
          </button>
          <button data-testid="moodboard-share" onClick={share} className="inline-flex items-center gap-1.5 text-sm border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100"><Share2 size={14} /> Share</button>
          <button data-testid="moodboard-export-png" onClick={() => doExport("png")} disabled={exporting} className="inline-flex items-center gap-1.5 text-sm border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100 disabled:opacity-40"><Download size={14} /> PNG</button>
          <button data-testid="moodboard-export-pdf" onClick={() => doExport("pdf")} disabled={exporting} className="inline-flex items-center gap-1.5 text-sm bg-zinc-950 text-white rounded-md px-3 py-1.5 hover:bg-zinc-800 disabled:opacity-40"><FileDown size={14} /> PDF</button>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* left panel */}
        <div className="w-72 shrink-0 bg-white border-r border-zinc-200 flex flex-col">
          {selected ? (
            <Inspector it={selected} onChange={(patch) => editItem(selected.id, patch)} onClose={() => setSelectedId(null)}
              onDelete={deleteSelected} onDuplicate={duplicateSelected} onBring={bring} />
          ) : (
            <AddPanel tab={tab} setTab={setTab} addItem={addItem} boardId={id} boardTheme={board.theme} />
          )}
        </div>

        {/* canvas */}
        <div className="flex-1 overflow-auto p-10 flex items-start justify-center" onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}>
          <div style={{ width: board.canvas_w * zoom, height: board.canvas_h * zoom }} className="shrink-0">
            <div style={{ width: board.canvas_w, height: board.canvas_h, transform: `scale(${zoom})`, transformOrigin: "top left" }}>
              <div ref={boardRef} data-testid="moodboard-canvas"
                onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}
                style={{ width: board.canvas_w, height: board.canvas_h, background: board.background, position: "relative" }}
                className="shadow-xl overflow-hidden">
                {items.map((it) => (
                  <div key={it.id} ref={(el) => { nodes.current[it.id] = el; }}
                    data-testid={`board-item-${it.id}`}
                    onMouseDown={(e) => { e.stopPropagation(); select(it.id); }}
                    style={{ ...itemTransform(it), cursor: "move" }}>
                    <BoardItemContent it={it} />
                  </div>
                ))}
              </div>
              {targetEl && !exporting && (
                <Moveable
                  target={targetEl} draggable resizable rotatable origin={false} keepRatio={false}
                  throttleDrag={0} throttleResize={0} throttleRotate={0}
                  onDragStart={(e) => e.set(frame.current.translate)}
                  onDrag={(e) => { frame.current.translate = e.beforeTranslate; e.target.style.transform = `translate(${e.beforeTranslate[0]}px, ${e.beforeTranslate[1]}px) rotate(${frame.current.rotate}deg)`; }}
                  onDragEnd={commitGeom}
                  onResizeStart={(e) => { e.setOrigin(["%", "%"]); if (e.dragStart) e.dragStart.set(frame.current.translate); }}
                  onResize={(e) => { frame.current.width = e.width; frame.current.height = e.height; frame.current.translate = e.drag.beforeTranslate; e.target.style.width = `${e.width}px`; e.target.style.height = `${e.height}px`; e.target.style.transform = `translate(${e.drag.beforeTranslate[0]}px, ${e.drag.beforeTranslate[1]}px) rotate(${frame.current.rotate}deg)`; }}
                  onResizeEnd={commitGeom}
                  onRotateStart={(e) => e.set(frame.current.rotate)}
                  onRotate={(e) => { frame.current.rotate = e.beforeRotate; e.target.style.transform = `translate(${frame.current.translate[0]}px, ${frame.current.translate[1]}px) rotate(${e.beforeRotate}deg)`; }}
                  onRotateEnd={commitGeom}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Inspector ----------------
function Inspector({ it, onChange, onClose, onDelete, onDuplicate, onBring }) {
  return (
    <div className="flex flex-col h-full" data-testid="moodboard-inspector">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">Edit {it.type}</span>
        <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100 text-zinc-500"><X size={15} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {it.type === "text" && (
          <>
            <textarea data-testid="inspector-text" value={it.text} onChange={(e) => onChange({ text: e.target.value })} rows={3}
              className="w-full border border-zinc-300 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
            <div className="flex items-center gap-2">
              <label className="text-xs text-zinc-500 w-16">Size</label>
              <input type="range" min="12" max="96" value={it.font_size} onChange={(e) => onChange({ font_size: Number(e.target.value) })} className="flex-1" />
              <span className="text-xs w-8 text-right">{it.font_size}</span>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-zinc-500 w-16">Color</label>
              <input type="color" value={it.color} onChange={(e) => onChange({ color: e.target.value })} className="h-8 w-12 rounded border border-zinc-200" />
              <button onClick={() => onChange({ bold: !it.bold })} className={`px-2 py-1 rounded text-xs border ${it.bold ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300"}`}>Bold</button>
              <select value={it.font_family} onChange={(e) => onChange({ font_family: e.target.value })} className="text-xs border border-zinc-300 rounded px-1.5 py-1">
                <option value="heading">Serif</option>
                <option value="body">Sans</option>
              </select>
            </div>
            <div className="flex gap-1">
              {["left", "center", "right"].map((a) => (
                <button key={a} onClick={() => onChange({ align: a })} className={`flex-1 py-1 rounded text-xs border capitalize ${it.align === a ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300"}`}>{a}</button>
              ))}
            </div>
          </>
        )}
        {it.type === "color" && (
          <>
            <div className="flex items-center gap-2">
              <input type="color" value={it.color} onChange={(e) => onChange({ color: e.target.value })} className="h-10 w-16 rounded border border-zinc-200" />
              <input value={it.color} onChange={(e) => onChange({ color: e.target.value })} className="flex-1 border border-zinc-300 rounded-md px-2 py-1.5 text-sm uppercase" />
            </div>
            <input data-testid="inspector-color-label" value={it.label || ""} onChange={(e) => onChange({ label: e.target.value })} placeholder="Swatch name" className="w-full border border-zinc-300 rounded-md px-2.5 py-2 text-sm" />
          </>
        )}
        {it.type === "image" && it.attribution?.name && (
          <p className="text-[11px] text-zinc-400">Photo by {it.attribution.name} on Unsplash.</p>
        )}
        <div className="pt-2 border-t border-zinc-100 space-y-2">
          <div className="flex gap-2">
            <button data-testid="inspector-forward" onClick={() => onBring(1)} className="flex-1 inline-flex items-center justify-center gap-1 text-xs border border-zinc-300 rounded-md py-1.5 hover:bg-zinc-100"><ChevronUp size={13} /> Forward</button>
            <button data-testid="inspector-back" onClick={() => onBring(-1)} className="flex-1 inline-flex items-center justify-center gap-1 text-xs border border-zinc-300 rounded-md py-1.5 hover:bg-zinc-100"><ChevronDown size={13} /> Back</button>
          </div>
          <button data-testid="inspector-duplicate" onClick={onDuplicate} className="w-full inline-flex items-center justify-center gap-1.5 text-xs border border-zinc-300 rounded-md py-1.5 hover:bg-zinc-100"><Copy size={13} /> Duplicate</button>
          <button data-testid="inspector-delete" onClick={onDelete} className="w-full inline-flex items-center justify-center gap-1.5 text-xs text-red-600 border border-red-200 rounded-md py-1.5 hover:bg-red-50"><Trash2 size={13} /> Delete</button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Add panel ----------------
function AddPanel({ tab, setTab, addItem, boardId, boardTheme }) {
  return (
    <div className="flex flex-col h-full">
      <AutoCompose boardId={boardId} boardTheme={boardTheme} />
      <div className="grid grid-cols-4 border-y border-zinc-200">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`mb-tab-${t.key}`} onClick={() => setTab(t.key)} title={t.label}
            className={`flex flex-col items-center gap-0.5 py-2 text-[10px] transition-colors ${tab === t.key ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {tab === "upload" && <UploadTab addItem={addItem} />}
        {tab === "ai" && <AITab addItem={addItem} />}
        {tab === "stock" && <StockTab addItem={addItem} />}
        {tab === "designs" && <SourceGrid endpoint="/designs" field="cover_image" labelField="title" addItem={addItem} empty="No designs yet." />}
        {tab === "materials" && <SourceGrid endpoint="/materials" field="image_url" labelField="name" addItem={addItem} empty="No materials yet." isMaterial />}
        {tab === "text" && (
          <button data-testid="mb-add-text" onClick={() => addItem({ type: "text", text: "Your text", width: 360, height: 70, font_size: 34, color: "#1a1a1a", font_family: "heading", align: "left", bold: true })}
            className="w-full bg-zinc-950 text-white rounded-md py-2.5 text-sm hover:bg-zinc-800">Add a text block</button>
        )}
        {tab === "color" && <ColorTab addItem={addItem} />}
      </div>
    </div>
  );
}

function AutoCompose({ boardId, boardTheme }) {
  const [theme, setTheme] = useState(boardTheme || "");
  const [styleId, setStyleId] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!theme.trim()) { toast.error("Enter a theme"); return; }
    setBusy(true);
    try {
      await api.post(`/moodboards/${boardId}/auto-compose`, { theme: theme.trim(), style_id: styleId || null });
      toast.success("Board drafted — reloading"); setTimeout(() => window.location.reload(), 500);
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  return (
    <div className="p-4 bg-gradient-to-br from-zinc-950 to-zinc-800 text-white">
      <p className="text-xs uppercase tracking-[0.15em] text-zinc-300 flex items-center gap-1.5"><Wand2 size={13} /> AI starter board</p>
      <input data-testid="mb-autocompose-theme" value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="Theme — e.g. romantic 90s revival"
        className="mt-2 w-full bg-white/10 border border-white/20 rounded-md px-2.5 py-2 text-sm placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-white/40" />
      <div className="mt-2 [&_label]:text-zinc-300"><StyleSelect value={styleId} onChange={setStyleId} dark /></div>
      <button data-testid="mb-autocompose-run" onClick={run} disabled={busy}
        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 bg-white text-zinc-950 rounded-md py-2 text-sm font-medium hover:bg-zinc-100 disabled:opacity-50">
        {busy ? <><Loader2 size={14} className="animate-spin" /> Composing…</> : <>Generate starter board</>}
      </button>
    </div>
  );
}

function UploadTab({ addItem }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const b64 = await fileToB64(file);
      const { data } = await api.post("/moodboards/upload", { image_b64: b64 });
      addItem({ type: "image", src: data.src, width: 320, height: 320 });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="block border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center cursor-pointer hover:border-zinc-500 hover:bg-zinc-50">
      <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="mb-upload-input" />
      {busy ? <Loader2 size={20} className="mx-auto animate-spin text-zinc-400" /> : <ImageIcon size={22} className="mx-auto text-zinc-400" />}
      <p className="text-xs text-zinc-500 mt-2">{busy ? "Uploading…" : "Click to upload an image"}</p>
    </label>
  );
}

function AITab({ addItem }) {
  const [prompt, setPrompt] = useState("");
  const [styleId, setStyleId] = useState("");
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    if (!prompt.trim()) { toast.error("Describe an image"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/moodboards/generate-image", { prompt: prompt.trim(), style_id: styleId || null });
      addItem({ type: "image", src: data.src, width: 340, height: 340 });
      toast.success("Image added");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <textarea data-testid="mb-ai-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} placeholder="Describe inspiration imagery — e.g. windswept linen on a Mediterranean terrace, golden hour"
        className="w-full border border-zinc-300 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
      <StyleSelect value={styleId} onChange={setStyleId} />
      <button data-testid="mb-ai-generate" onClick={gen} disabled={busy} className="w-full inline-flex items-center justify-center gap-1.5 bg-zinc-950 text-white rounded-md py-2.5 text-sm hover:bg-zinc-800 disabled:opacity-40">
        {busy ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Generate image</>}
      </button>
    </div>
  );
}

function StockTab({ addItem }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const search = async () => {
    if (!q.trim()) return;
    setBusy(true); setMsg("");
    try {
      const { data } = await api.get(`/moodboards/stock/search`, { params: { q: q.trim() } });
      setResults(data.results); if (data.results.length === 0) setMsg("No photos found.");
    } catch (e) { setResults([]); setMsg(apiError(e)); } finally { setBusy(false); }
  };
  const add = async (p) => {
    addItem({ type: "image", src: p.full, width: 320, height: 320, attribution: { name: p.photographer_name, url: p.photographer_url } });
    if (p.download_location) api.post("/moodboards/stock/track", { download_location: p.download_location }).catch(() => {});
    toast.success("Photo added");
  };
  return (
    <div className="space-y-3">
      <form onSubmit={(e) => { e.preventDefault(); search(); }} className="flex gap-1.5">
        <input data-testid="mb-stock-query" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search photos…"
          className="flex-1 border border-zinc-300 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
        <button data-testid="mb-stock-search" className="px-3 bg-zinc-950 text-white rounded-md text-sm">{busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}</button>
      </form>
      {msg && <p className="text-[11px] text-zinc-500 bg-zinc-100 rounded p-2">{msg}</p>}
      <div className="grid grid-cols-2 gap-2">
        {results.map((p) => (
          <button key={p.id} data-testid={`mb-stock-${p.id}`} onClick={() => add(p)} className="relative rounded-md overflow-hidden border border-zinc-200 hover:ring-2 hover:ring-zinc-950">
            <img src={p.thumb} alt="" className="w-full h-24 object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

function SourceGrid({ endpoint, field, labelField, addItem, empty, isMaterial }) {
  const [list, setList] = useState(null);
  useEffect(() => { api.get(endpoint).then((r) => setList(r.data)).catch(() => setList([])); }, [endpoint]);
  if (list === null) return <div className="flex justify-center py-6"><Loader2 className="animate-spin text-zinc-300" size={18} /></div>;
  const usable = list.filter((x) => x[field]);
  if (usable.length === 0) return <p className="text-xs text-zinc-400 text-center py-6">{empty}</p>;
  return (
    <div className="grid grid-cols-2 gap-2">
      {usable.map((x) => (
        <button key={x.id} data-testid={`mb-source-${x.id}`} title={x[labelField]}
          onClick={() => addItem({ type: "image", src: x[field], width: isMaterial ? 220 : 300, height: isMaterial ? 220 : 340 })}
          className="relative rounded-md overflow-hidden border border-zinc-200 hover:ring-2 hover:ring-zinc-950">
          {x.source === "plm" && <span className="absolute top-1 left-1 bg-zinc-950 text-white text-[8px] uppercase px-1 rounded z-10">PLM</span>}
          <img src={imgUrl(x[field])} alt="" className="w-full h-24 object-cover" />
          <p className="text-[9px] p-1 truncate text-left">{x[labelField]}</p>
        </button>
      ))}
    </div>
  );
}

function ColorTab({ addItem }) {
  const [color, setColor] = useState("#c9b8a3");
  const [label, setLabel] = useState("");
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input type="color" data-testid="mb-color-picker" value={color} onChange={(e) => setColor(e.target.value)} className="h-12 w-16 rounded border border-zinc-200" />
        <input value={color} onChange={(e) => setColor(e.target.value)} className="flex-1 border border-zinc-300 rounded-md px-2 py-2 text-sm uppercase" />
      </div>
      <input data-testid="mb-color-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Color name (optional)" className="w-full border border-zinc-300 rounded-md px-2.5 py-2 text-sm" />
      <button data-testid="mb-add-color" onClick={() => addItem({ type: "color", color, label, width: 120, height: 150 })}
        className="w-full bg-zinc-950 text-white rounded-md py-2.5 text-sm hover:bg-zinc-800">Add color swatch</button>
    </div>
  );
}
